import { ApiErrorHandler } from "api-client";
import type { AssignCarrierSubmitPayload, BookingRow } from "design-components";
import { isAssignCarrierReassign } from "design-components";
import { bookingsService } from "../../../shared/services/bookings.service";
import { buildAssignCarrierRequest } from "./assign-carrier-payload.mapper";

export type AssignCarrierNotificationInput = {
  variant: "error" | "success";
  title: string;
  description: string;
};

export type ExecuteAssignCarrierSubmitOptions = {
  row: BookingRow;
  payload: AssignCarrierSubmitPayload;
  showNotification: (input: AssignCarrierNotificationInput) => void;
  onSuccess: () => void;
  /** When set, error/success toasts use these titles; bookings list uses empty titles. */
  notificationTitles?: {
    error?: string;
    success?: string;
    inviteSuccess?: string;
  };
};

/** Shared assign-carrier submit (FR-AV168-010, NFR-AV168-002). Rejects on API or transport failure. */
export async function executeAssignCarrierSubmit(
  options: ExecuteAssignCarrierSubmitOptions,
): Promise<void> {
  const { row, payload, showNotification, onSuccess, notificationTitles = {} } = options;

  if (!row.bookingNumber?.trim()) {
    throw new Error("Booking not found. Refresh the list and try again.");
  }

  const bookingId = Number(payload.bookingId);
  if (!Number.isFinite(bookingId)) {
    throw new Error("Invalid booking id.");
  }

  const isReassign = isAssignCarrierReassign(row);
  const assignFailureFallback = isReassign
    ? "Failed to reassign carrier."
    : "Failed to assign carrier.";
  const errorTitle = notificationTitles.error ?? "";
  const successTitle = notificationTitles.success ?? "";
  const inviteSuccessTitle = notificationTitles.inviteSuccess ?? successTitle;

  try {
    const body = buildAssignCarrierRequest(bookingId, payload, row);
    const res = await bookingsService.assignCarrierToBooking(body);
    if (res.statusCode !== 200) {
      throw new Error(res.message?.trim() || assignFailureFallback);
    }

    const label =
      payload.mode === "existingCarrier"
        ? payload.carrierLabel
        : res.data.carrierName || payload.carrierName;
    const bookingNumber = res.data.bookingNumber || row.bookingNumber;

    showNotification({
      variant: "success",
      title: successTitle,
      description: `Carrier "${label}" is Assigned to the booking "${bookingNumber}".`,
    });

    if (payload.mode === "invitedCarrier") {
      showNotification({
        variant: "success",
        title: inviteSuccessTitle,
        description: `Invite sent to ${label}.`,
      });
    }

    onSuccess();
  } catch (err: unknown) {
    const description = ApiErrorHandler.extractAxiosError(err, assignFailureFallback);
    showNotification({
      variant: "error",
      title: errorTitle,
      description,
    });
    throw new Error(description);
  }
}
